"use client";
import EditorIsland from "@/components/EditorIsland";

/**
 * Inline-editable image with full admin controls.
 *
 * v6.13.14 — Major upgrade. Now stores FOUR content blocks per image
 * (the original `id` plus three derived keys), so admins can not only
 * swap the image but also apply an animation template, scale it, and
 * carve / add space below it. When wrapped in an <EditableImageGroup>,
 * the popover also exposes Move-Up / Move-Down buttons that reorder
 * the image within the group.
 *
 *   {id}        → src                          (string URL or data URL)
 *   {id}.anim   → animation template class     (e.g. "atpl-float")
 *   {id}.scale  → scale multiplier             (string float, "0.50".."2.00")
 *   {id}.mb     → margin below image in px     (string int, "-200".."400")
 *
 * Default mode: renders an `<img>` (we use `<img>` rather than next/image
 * because the URL is dynamic, can be a data URL, and we don't want to
 * pre-build a remote-pattern allowlist for every image an admin pastes).
 *
 * Edit mode: clicking opens a popover with all five controls. Every
 * change flows through the existing `setValue(id, …)` so undo / redo /
 * dirty tracking / Save (Publish) all work for these new keys exactly
 * like text edits.
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, DragEvent as ReactDragEvent } from "react";
import { useEditContext } from "@/lib/edit-context";
import { useEditableImageGroup } from "./EditableImageGroup";
import { ANIMATION_TEMPLATES } from "@/lib/image-presets";
import MoveHandle, { useMoveOffset as useMoveOffsetEditable } from "@/components/MoveHandle";

/**
 * v6.14 — Route remote images through the on-server cache proxy
 * (`/api/img`) on the PUBLIC render path. This gives every admin-pasted
 * image long-lived browser + CDN caching (so attraction / brand photos
 * stop reloading on every visit) AND fixes images whose host blocks
 * hot-linking by Referer (the proxy fetches server-side with no browser
 * Referer). Deterministic on server & client (no `window`) to avoid
 * hydration mismatch. Left untouched: data:/blob:/relative URLs and
 * anything already pointing at our own image routes.
 */
/**
 * v6.14.1 — Fast-path known hot-linkable CDN hosts.
 *
 * Default brand logos / favicons come from a handful of public image CDNs
 * (Wikimedia's media host + Google's favicon service) that are designed to
 * be hot-linked, never block by Referer, and already ship long-lived cache
 * headers + a global edge. Routing those through `/api/img` only HURTS: it
 * forces a serial extra round-trip per image (browser → our single Render
 * instance → upstream), so a grid of brand logos pops in slowly on first
 * visit because every request queues through one server. We already prove
 * direct loading works for these exact URLs in <BrandLogo>'s plain <img>.
 * So load them straight from the source CDN (massively parallel, edge-fast)
 * and keep the proxy ONLY for admin-pasted arbitrary-host images, which is
 * where the hot-link fixes + caching actually matter. Deterministic on
 * server & client (no `window`) to avoid hydration mismatch.
 */
const DIRECT_IMG_HOSTS = new Set(["upload.wikimedia.org", "www.google.com", "google.com"]);
function isDirectImgHost(host: string): boolean {
  const h = host.toLowerCase();
  return DIRECT_IMG_HOSTS.has(h) || h.endsWith(".gstatic.com");
}
function cachedSrc(u: string): string {
  if (!u || !/^https?:\/\//i.test(u)) return u;
  if (/^https?:\/\/[^/]+\/(api\/img|gc-img|_next)/i.test(u)) return u;
  try {
    if (isDirectImgHost(new URL(u).hostname)) return u;
  } catch {
    /* malformed URL → fall through to the proxy */
  }
  return `/api/img?u=${encodeURIComponent(u)}`;
}

type Props = {
  id: string;
  defaultSrc: string;
  alt: string;
  className?: string;
  /** Sizing wrapper class so the popover anchors correctly. */
  wrapperClassName?: string;
  /** Optional inline style for the wrapper (e.g. fixed sizing). */
  wrapperStyle?: CSSProperties;
  /** Load eagerly (above-the-fold images) instead of the default lazy. */
  eager?: boolean;
};

function EditableImageInner({
  id,
  defaultSrc,
  alt,
  className = "",
  wrapperClassName = "inline-block",
  wrapperStyle,
  eager = false,
}: Props) {
  const { isAdmin, editMode, getValue, setValue } = useEditContext();
  const group = useEditableImageGroup();

  const src   = getValue(id, defaultSrc);
  const anim  = getValue(`${id}.anim`,  "");
  const scale = parseFloat(getValue(`${id}.scale`, "1") || "1");
  const mb    = parseInt(getValue(`${id}.mb`, "0") || "0", 10);

  const editing = isAdmin && editMode;

  const [popOpen, setPopOpen] = useState(false);
  const [draftSrc, setDraftSrc] = useState(src);
  const popRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  /* v6.13.43 — Anchor rect for the portal-rendered popover. We re-measure
     on open + on every scroll/resize so the popover follows the image
     even when the user scrolls the page mid-edit. */
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  useEffect(() => setDraftSrc(src), [src]);

  // Click-outside dismiss + anchor tracking while popover is open.
  useEffect(() => {
    if (!popOpen) return;
    const measure = () => {
      /* v6.13.44 — Anchor to the IMG rect (not the wrapper) because
         the wrapper's box ignores the image's CSS scale transform.
         When scale > 1 the image bleeds beyond the wrapper, so a
         popover anchored to the wrapper would sit ON TOP of the
         visible image — producing the "image vanishes when I scale"
         report. The IMG rect includes the scale transform, so the
         popover always lands clear of the rendered image. */
      const r =
        imgRef.current?.getBoundingClientRect() ??
        wrapperRef.current?.getBoundingClientRect();
      if (r) setAnchorRect(r);
    };
    measure();
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current && popRef.current.contains(t)) return;
      if (wrapperRef.current && wrapperRef.current.contains(t)) return;
      setPopOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [popOpen]);

  const applySrc = (next: string) => {
    if (next !== src) setValue(id, next);
    setPopOpen(false);
  };

  /* v6.13.58 — File uploads now POST to /api/admin/upload and store
     the returned `/uploads/<hash>.<ext>` URL, instead of reading the
     file as a base64 data URL.

     Why this matters: the previous FileReader path stored a ~5.3 MB
     text blob (for a 4 MB image) inside the content_blocks row. When
     the admin then clicked Publish, that blob was packed into the
     PUT /api/admin/content JSON body together with every other
     pending edit. Render / Next.js silently rejects bodies past
     ~4 MB, so the entire batch failed — and because the toolbar
     reports a single boolean from flush(), every queued edit
     (the new image src AND the scale/animation/margin tweaks the
     user made in the same popover) appeared to "revert" the moment
     they exited edit mode and refreshed. Routing through the
     dedicated upload endpoint keeps the PUT body tiny and lets
     all pending edits actually persist. */
  const onFile = async (file: File) => {
    if (file.size > 8 * 1024 * 1024) {
      alert("Image must be under 8 MB.");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/admin/upload", {
        method: "POST",
        credentials: "same-origin",
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        alert(`Upload failed: ${(j as { error?: string }).error ?? res.status}`);
        return;
      }
      const { url } = (await res.json()) as { url: string };
      applySrc(url);
    } catch (err) {
      console.error("[editable-image] upload failed", err);
      alert("Upload failed. Check the console for details.");
    }
  };

  /* v6.13.41 — Scale is now applied to the IMG element, NOT to the
     outer wrapper.

     Old behaviour: `transform: scale(N)` was applied to the wrapper
     <span> that contains BOTH the image and the edit popover. Two
     consequences the user reported:
       (a) "Editing scale of image does not resize image" — true in a
           layout sense: CSS transforms don't reflow, so the wrapper
           still occupied its original bounding box and surrounding
           siblings didn't move out of the way. Visually the image
           appeared the same size relative to the (now-overlapping)
           neighbours.
       (b) "Breaks usability of the entire box to edit" — the popover
           is a child of the wrapper, so it inherited the same scale
           transform. At scale 0.5 the popover became unreadable; at
           scale 2.0 it overflowed the viewport and the file picker
           / Apply button were unreachable.

     New behaviour: the wrapper keeps spacing + group order only. The
     scale transform is applied directly to the <img>, with
     `transform-origin: center top` so the image grows DOWNWARD
     (instead of bleeding upward into the previous section) and the
     popover — which is anchored to the wrapper — always renders at
     1× and remains usable regardless of image scale. */
  /* v6.13.42 — Per-element drag-to-reposition. The persisted (dx,dy)
     from useMoveOffset is composed into the wrapper transform along
     with any caller-supplied transform, so admins can drag the whole
     image to a new spot via the MoveHandle rendered below. */
  const move = useMoveOffsetEditable(id);
  // v6.13.62 — Admin-only drag offset. Previously the persisted dx/dy
  // were applied for ALL visitors, which caused tier-1 pricing image
  // to drift over the title text after an admin had ever nudged it.
  // Non-admins now always see the un-translated image.
  const wrapperTransform = (isAdmin && editMode) ? move.transform : undefined;
  const compoundStyle: CSSProperties = {
    ...wrapperStyle,
    ...(wrapperTransform ? { transform: wrapperTransform } : {}),
    // v6.13.63 — Admin-only margin offset. Same fix as wrapperTransform:
    // non-admins always see the un-margined image so admin-stored mb cannot
    // push pricing tier images down into the next section's text.
    ...((isAdmin && editMode) && mb !== 0 ? { marginBottom: `${mb}px` } : {}),
    ...(group ? { order: group.indexOf(id) } : {}),
  };

  // v6.13.63 — Admin-only scale. Admin-stored scale was applied to ALL
  // visitors, so a scaled-up pricing tier image would overlap its title
  // for everyone, not just admins. Non-admins now always see scale 1.
  const visitorScale = (isAdmin && editMode) ? scale : 1;
  const imgScaleStyle: CSSProperties =
    visitorScale !== 1
      ? {
          transform: `scale(${visitorScale})`,
          transformOrigin: "center top",
          // Reserve vertical room for the scaled image so the next
          // section actually moves down/up — transforms don't reflow,
          // but margin does. Negative scale (<1) pulls following
          // content up; >1 pushes it down. Approximate the visual
          // delta as (scale-1) × natural height, but since we don't
          // know the natural height ahead of time, fall back to a
          // best-effort using the rendered offsetHeight via CSS only
          // (`em` units would require a font ref). Leave the height
          // reserve responsibility to the admin's `Space below`
          // slider — already exposed via `mb` — and just document
          // the trade-off here.
        }
      : {};

  /* v6.13.35 — Drag-and-drop affordances. Two distinct behaviours
     are wired into the SAME wrapper element:

     (1) Internal sibling reorder (only when this image lives inside
         an <EditableImageGroup>). The wrapper becomes draggable in
         admin/edit mode; onDragStart/Over/Drop talk to the group
         context which mutates the persisted order array.

     (2) External file drop. Admins can drag an image file straight
         off their desktop onto ANY EditableImage to set that
         image's source — no need to open the popover and click
         "Upload file…". This works on every EditableImage, group
         or no group.

     Both share the same onDrop handler; we discriminate by checking
     `e.dataTransfer.types` — "Files" wins (user is dropping a real
     file from the OS), otherwise it's an internal sibling drag.
     `isDragOver` drives a visible amber ring so the admin gets
     unambiguous feedback about where the drop will land. */
  const [isDragOver, setIsDragOver] = useState(false);
  const draggable = editing && Boolean(group);
  const showOverRing = isDragOver || (group?.overId === id);

  const onWrapperDragStart = (e: ReactDragEvent) => {
    if (!draggable || !group) return;
    group.startDrag(id);
    // Required by Firefox to actually start a drag.
    e.dataTransfer.setData("text/plain", id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onWrapperDragEnd = () => {
    if (group) group.endDrag();
    setIsDragOver(false);
  };
  const onWrapperDragOver = (e: ReactDragEvent) => {
    if (!editing) return;
    const types = Array.from(e.dataTransfer.types);
    const isFile = types.includes("Files");
    const isReorder = group && group.dragId && group.dragId !== id;
    if (!isFile && !isReorder) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = isFile ? "copy" : "move";
    if (isReorder && group) group.hoverDrag(id);
    setIsDragOver(true);
  };
  const onWrapperDragLeave = () => setIsDragOver(false);
  const onWrapperDrop = (e: ReactDragEvent) => {
    if (!editing) return;
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      onFile(file);
      return;
    }
    if (group && group.dragId && group.dragId !== id) {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      group.dropOn(id);
    }
  };

  /* v6.13.44 — Side-aware popover placement so it never sits on top
     of the (possibly scaled) image:
       • Prefer placing it to the RIGHT of the image, vertically
         aligned to the image top.
       • If there's not enough room on the right, try the LEFT.
       • If neither side fits, fall back to BELOW the image.
     All three placements use the IMG rect (which includes CSS scale)
     so the popover stays fully clear of the visible image. Width
     fixed to 22rem (352px). */
  const POP_W = 352;
  const POP_H_EST = 420;
  const GAP = 12;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  let popLeft = 0;
  let popTop = 0;
  if (anchorRect) {
    const spaceRight = vw - anchorRect.right;
    const spaceLeft = anchorRect.left;
    if (spaceRight >= POP_W + GAP + 8) {
      popLeft = anchorRect.right + GAP;
      popTop = Math.max(8, Math.min(anchorRect.top, vh - POP_H_EST - 8));
    } else if (spaceLeft >= POP_W + GAP + 8) {
      popLeft = anchorRect.left - POP_W - GAP;
      popTop = Math.max(8, Math.min(anchorRect.top, vh - POP_H_EST - 8));
    } else {
      popLeft = Math.max(8, Math.min(anchorRect.left, vw - POP_W - 8));
      popTop = Math.min(anchorRect.bottom + GAP, vh - POP_H_EST - 8);
      if (popTop < 8) popTop = 8;
    }
  }

  return (
    <span
      ref={wrapperRef}
      className={`relative ${wrapperClassName} ${
        editing
          ? "rounded-lg outline outline-2 outline-offset-2 transition-[outline-color,box-shadow] " +
            (showOverRing
              ? "outline-amber-300 [box-shadow:0_0_0_4px_rgba(245,185,69,0.18)]"
              : "outline-transparent")
          : ""
      }`}
      style={compoundStyle}
      data-move-target={id}
      draggable={draggable}
      onDragStart={onWrapperDragStart}
      onDragEnd={onWrapperDragEnd}
      onDragOver={onWrapperDragOver}
      onDragLeave={onWrapperDragLeave}
      onDrop={onWrapperDrop}
      title={
        editing
          ? group
            ? "Click to edit • Drag to reorder • Drop an image file to replace"
            : "Click to edit • Drop an image file to replace"
          : undefined
      }
    >
      {editing && (
        <MoveHandle
          id={id}
          positionClassName="-right-3 -top-3"
          /* v6.13.53 — When this image is part of an
             EditableImageGroup, releasing the MoveHandle drag over
             a sibling reorders the group via CSS `order` instead
             of leaving a translate3d offset. Cleanly closes the
             "blank gap where I moved it from / overlap at the
             destination" report on both desktop and mobile. */
          /* v6.13.61 — onDropTo intentionally NOT wired. The MoveHandle
               pointer-drag is a precise free-position tool. Group reorder
               is still available via the wrapper's HTML5 drag-and-drop
               and the up/down arrows in the admin popover. */
        />
      )}
      {/* v6.13.45 — Scale lives on an OUTER span, animation lives on the
          <img>. Animation templates (float / rotate / pulse / etc.)
          drive the `transform` property via CSS keyframes; an animated
          property always overrides any inline `transform` on the SAME
          element. Splitting them onto two elements means the scale
          survives even when an animation template is selected. */}
      <span
        className="block h-full w-full"
        style={imgScaleStyle}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          ref={imgRef}
          src={editing ? (src || defaultSrc) : cachedSrc(src || defaultSrc)}
          alt={alt}
          className={
            (anim ? `${anim} ` : "") +
            (editing
              ? `${className} cursor-pointer outline outline-2 outline-transparent transition-[outline-color] hover:outline-amber-300/80`
              : className)
          }
          data-editable-id={id}
          onClick={(e) => {
            if (!editing) return;
            e.preventDefault();
            e.stopPropagation();
            setPopOpen((o) => !o);
          }}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          draggable={false}
        />
      </span>

      {editing && popOpen && anchorRect && typeof document !== "undefined" && createPortal(
        <div
          ref={popRef}
          data-lenis-prevent
          style={{ position: "fixed", top: popTop, left: popLeft, width: POP_W, zIndex: 100000, maxHeight: "min(80vh, 640px)" }}
          className="space-y-3 overflow-y-auto overscroll-contain rounded-xl border border-white/15 bg-ink-900/98 p-3 text-xs text-white shadow-2xl backdrop-blur-xl"
        >
          <div className="text-[10px] uppercase tracking-widest text-white/45">
            Image · {id}
          </div>

          {/* ── 1. SRC swap ───────────────────────────────────────── */}
          <div className="space-y-2">
            <input
              value={draftSrc}
              onChange={(e) => setDraftSrc(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applySrc(draftSrc);
                if (e.key === "Escape") setPopOpen(false);
              }}
              placeholder="https://… or paste a data URL"
              className="w-full rounded-md border border-white/10 bg-white/10 px-2 py-1.5 text-white caret-amber-300 outline-none placeholder:text-white/40 focus:border-amber-300/60 focus:bg-white/15"
              spellCheck={false}
            />
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-white/80 hover:bg-white/10"
              >
                Upload file…
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onFile(f);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => applySrc(draftSrc)}
                className="rounded-md bg-amber-400 px-3 py-1 font-semibold text-ink-950 hover:brightness-110"
              >
                Apply src
              </button>
            </div>
          </div>

          {/* ── 2. ANIMATION TEMPLATE ─────────────────────────────── */}
          <label className="block">
            <span className="mb-1 block text-[10px] uppercase tracking-widest text-white/45">
              Animation template
            </span>
            <select
              value={anim}
              onChange={(e) => setValue(`${id}.anim`, e.target.value)}
              className="w-full rounded-md border border-white/10 bg-white/10 px-2 py-1.5 text-white caret-amber-300 outline-none focus:border-amber-300/60 focus:bg-white/15"
            >
              <option value="" className="bg-ink-900 text-white">— None —</option>
              {ANIMATION_TEMPLATES.map((t) => (
                <option key={t.cls} value={t.cls} className="bg-ink-900 text-white">{t.label}</option>
              ))}
            </select>
          </label>

          {/* ── 3. SCALE slider ───────────────────────────────────── */}
          <label className="block">
            <span className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-widest text-white/45">
              <span>Scale</span><span className="text-white/70">{scale.toFixed(2)}×</span>
            </span>
            <input
              type="range" min={0.5} max={2.0} step={0.05} value={scale}
              onChange={(e) => setValue(`${id}.scale`, e.target.value)}
              className="w-full accent-amber-400"
            />
          </label>

          {/* ── 4. SPACING slider (manual cut/add) ────────────────── */}
          <label className="block">
            <span className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-widest text-white/45">
              <span>Space below (px)</span><span className="text-white/70">{mb}</span>
            </span>
            <input
              type="range" min={-200} max={400} step={4} value={mb}
              onChange={(e) => setValue(`${id}.mb`, e.target.value)}
              className="w-full accent-amber-400"
            />
          </label>

          {/* ── 5. REORDER (only when inside an EditableImageGroup) ── */}
          {group && (
            <div className="flex items-center justify-between rounded-md border border-white/10 bg-white/5 px-2 py-1.5">
              <span className="text-white/65">
                Position: <span className="text-white/95">{group.indexOf(id) + 1} / {group.order.length}</span>
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => group.moveUp(id)}
                  disabled={group.indexOf(id) <= 0}
                  className="rounded-md border border-white/10 px-2 py-0.5 text-white/80 hover:bg-white/10 disabled:opacity-40"
                  aria-label="Move earlier"
                >↑</button>
                <button
                  type="button"
                  onClick={() => group.moveDown(id)}
                  disabled={group.indexOf(id) >= group.order.length - 1}
                  className="rounded-md border border-white/10 px-2 py-0.5 text-white/80 hover:bg-white/10 disabled:opacity-40"
                  aria-label="Move later"
                >↓</button>
              </div>
            </div>
          )}

          <div className="flex justify-between border-t border-white/10 pt-2">
            <button
              type="button"
              onClick={() => {
                setValue(`${id}.anim`,  "");
                setValue(`${id}.scale`, "1");
                setValue(`${id}.mb`,    "0");
              }}
              className="text-white/55 hover:text-white"
            >
              Reset effects
            </button>
            <button
              type="button"
              onClick={() => setPopOpen(false)}
              className="text-white/85 hover:text-white"
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
    </span>
  );
}

export default function EditableImage(props: Props) {
  return (
    <EditorIsland id={props.id}>
      <EditableImageInner {...props} />
    </EditorIsland>
  );
}
